import {
  IconAnalytics,
  IconBell,
  IconBinocularsGray,
  IconChainLink,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import { publicCrosswalk1, recommendationEdge, recommendationSwitch } from '@assets/images/solution/recommendation';
import mainVideo from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/publicCrosswalk/img/1.avif';
import image2 from '@src/assets/images/solution/publicCrosswalk/img/2.avif';
import image3 from '@src/assets/images/solution/publicCrosswalk/img/3.avif';
import image4 from '@src/assets/images/solution/publicCrosswalk/img/4.avif';
import image5 from '@src/assets/images/solution/publicCrosswalk/img/5.avif';
import image6 from '@src/assets/images/solution/publicCrosswalk/img/6.avif';
import image7 from '@src/assets/images/solution/publicCrosswalk/img/7.avif';
import recommendation from '../common/recommendation';

export default {
  type: 'Urban Safety Solution',

  recommend: {
    title: '',
    desc: 'ORBRO OS is a powerful platform for real‑time location tracking and smart monitoring.<br/>It seamlessly integrates BLE, UWB, and AI‑based video analytics to enhance safety, efficiency, and scalability in industrial environments.',
    labels: {
      desktop: [
        { img: publicCrosswalk1, data: recommendation.ptzCamera },
        { img: recommendationEdge, data: recommendation.edgeRtls },
        { img: recommendationSwitch, data: recommendation.switch },
      ],
      mobile: [{ data: recommendation.ptzCamera }, { data: recommendation.edgeAi }, { data: recommendation.switch }],
    },
  },

  banner: {
    title: 'Prevent crosswalk accidents and ensure pedestrian safety.',
    desc: 'AI detects movements of vehicles and pedestrians and immediately issues warnings via digital signage and speakers when a collision risk arises.',
  },

  numOfCard: {
    title: 'Use AI to eliminate crosswalk accidents entirely.',
    desc: 'With real‑time object detection and two‑way alerts, pedestrian accidents at right‑turn intersections are reduced to zero.',
    card: [
      {
        tag: '99.9%',
        title: 'Object detection accuracy',
        desc: 'Accurately detects pedestrians, bicycles, scooters, and more.',
      },
      {
        tag: '100+',
        title: 'Number of simultaneous detected objects',
        desc: 'Tracks multiple objects in real time.',
      },
      {
        tag: '0',
        title: 'Fatal accidents since installation',
        desc: 'No deaths at right‑turn intersections after system installation.',
      },
      {
        tag: '0',
        title: 'Injury accidents since installation',
        desc: 'No collisions following detection and alert.',
      },
    ],
  },

  func: [
    {
      theme: 'Precisely detect vehicle–pedestrian collision risks and alert instantly.',
      title: 'Real‑time AI analysis of vehicles and pedestrians',
      desc: 'AI identifies vehicle and pedestrian positions, speed, and direction in real time. It accurately detects children, strollers, bicycles, scooters, even in limited‑visibility zones.',
      sub1: 'High detection accuracy',
      icon1: IconRulerGray,
      sub2: 'Real‑time status monitoring',
      icon2: IconClockGray,
      image: image2,
    },
    {
      title: 'Collision risk assessed from relative location and direction',
      desc: 'The system calculates trajectories, speed, and direction, and only alerts when paths intersect or objects are within close range—ensuring accurate warnings.',
      sub2: 'Real‑time location tracking',
      icon1: IconBinocularsGray,
      sub1: 'Fast data sync',
      icon2: IconTvocGray,
      image: image3,
    },
    {
      title: 'Two‑way alerts via digital signage and speaker systems',
      desc: 'Digital signs at both sides of intersections alert drivers to pedestrians and vice versa. This bidirectional alerting significantly reduces accident risk.',
      sub1: 'Real‑time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High detection accuracy',
      icon2: IconRulerGray,
      image: image4,
    },
    {
      theme: 'Efficient deployment and easy operation for optimal safety.',
      title: 'Edge‑AI for autonomous, secure data processing',
      desc: 'On‑site server installation ensures data protection and prevents leaks. Operated on‑premise for strong privacy and efficient management.',
      sub1: 'Flexible zone configuration',
      icon1: IconZoneGray,
      sub2: 'Support for multiple zones',
      icon2: IconTagGray,
      image: image5,
    },
    {
      title: 'Custom installation in high‑risk areas',
      desc: 'Deploy the AI video analytics system at high‑accident intersections or school zones. Tailored to local context for effective safety management.',
      sub1: 'Flexible zone configuration',
      icon1: IconZoneGray,
      sub2: 'Support for multiple zones',
      icon2: IconTagGray,
      image: image6,
    },
    {
      title: 'Supports integration with various camera brands',
      desc: 'Easily integrates with cameras from different manufacturers, enabling reuse of existing infrastructure and simple future upgrades.',
      sub1: 'Flexible zone configuration',
      icon1: IconZoneGray,
      sub2: 'Support for multiple zones',
      icon2: IconTagGray,
      image: image7,
    },
  ],

  subfunc: {
    title: 'Feature overview',
    desc: 'Ensuring pedestrian safety boosts efficiency and mitigates losses. ORBRO equips urban environments with specialized safety features for hazard zones and accident‑prone areas.',
    data: [
      {
        icon: IconSearch,
        title: 'Multi‑object detection',
        desc: 'Accurately detects multiple objects including children, strollers, bicycles, and scooters.',
      },
      {
        icon: IconRoute,
        title: 'Real‑time position and trajectory tracking',
        desc: 'Monitors each object’s location, speed, and direction in real time.',
      },
      {
        icon: IconAnalytics,
        title: 'Collision‑risk assessment algorithm',
        desc: 'Calculates collision likelihood based on distance and trajectory intersections.',
      },
      {
        icon: IconChartBar,
        title: 'Two‑way alert delivery',
        desc: 'Signs alert drivers, speakers alert pedestrians simultaneously.',
      },
      {
        icon: IconLayers,
        title: 'Blind‑spot coverage',
        desc: 'Detects pedestrians even in limited‑visibility intersections and right‑turn zones.',
      },
      {
        icon: IconAnalytics,
        title: 'Precision alert thresholds',
        desc: 'Customizes distance and speed thresholds to minimize false alarms.',
      },
      {
        icon: IconLayers,
        title: 'On‑premise data security',
        desc: 'Processes data locally without external transmission for enhanced protection.',
      },
      {
        icon: IconSearch,
        title: 'Easy CCTV integration',
        desc: 'Compatible with many camera brands for seamless integration with existing systems.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Lineup',
  product: [{ tag: 'workerPackage' }, { tag: 'assetPackage' }],

  contents: [{ data: 'constructionAsset' }, { data: 'constructionEquipment' }, { data: 'blogRTLS' }, { data: 'blogMedical' }],
};
